// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * PrimaryActionButton — centrale design-systemcomponent voor de primaire
 * vervolgactie binnen WilsApp (Volgende, Afronden, Klaar, Terug naar [bron], enz.).
 *
 * Eén uniforme huisstijl, gebaseerd op de prominente wizard-"Volgende"-knop:
 *  - groene achtergrond (bg-moss), hover bg-moss-light
 *  - vaste hoogte via px-6 py-4, afgeronde hoeken rounded-2xl
 *  - lettergrootte text-base, letterdikte font-semibold
 *  - consistente hover-, focus- en disabled-states en active-animatie
 *
 * Gebruik deze component voor ELKE primaire vervolgactie; geen losse CSS.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface PrimaryActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icoon links van het label (lucide-icoon, h-5 w-5). */
  icon?:      ReactNode
  /** Icoon rechts van het label (bv. pijl-rechts bij "Volgende"). */
  iconRight?: ReactNode
  /** Volle breedte (standaard true; zet op false in een flex-rij naast een andere knop). */
  fullWidth?: boolean
}

export const PrimaryActionButton = forwardRef<HTMLButtonElement, PrimaryActionButtonProps>(
  ({ icon, iconRight, fullWidth = true, className, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center gap-2.5 rounded-2xl px-6 py-4',
        'text-base font-semibold transition-all duration-150 active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sage-600',
        disabled
          ? 'bg-sand-300 text-ink-4 cursor-not-allowed pointer-events-none'
          : 'bg-moss text-white hover:bg-moss-light shadow-elevated',
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children && <span>{children}</span>}
      {iconRight && <span className="flex-shrink-0">{iconRight}</span>}
    </button>
  ),
)

PrimaryActionButton.displayName = 'PrimaryActionButton'
